import { getIntlTranslation } from '@/utils/intl';
import { Select } from 'antd';
import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { fragmentDisplayName } from './EmailBodyEditor/labels';

import styles from './FragmentInsertSelect.less';

export interface InsertableFragment {
  key: string;
  type: string;
}

interface FragmentInsertSelectProps {
  fragments: InsertableFragment[];
  /** Receives the ready-to-insert `{% include %}` statement and the key. */
  onInsert: (statement: string, key: string) => void;
  disabled?: boolean;
}

/**
 * Searchable picker over the partner's building blocks (partial fragments).
 * Selecting one hands the caller its `{% include 'key' %}` statement — the
 * body editor inserts it at the caret, mirroring the translation picker.
 * Stateless: it clears after every pick so repeated inserts work. Layouts are
 * excluded — they belong in the template's layout field, not inline.
 */
const FragmentInsertSelect: React.FC<FragmentInsertSelectProps> = ({
  fragments,
  onInsert,
  disabled,
}) => {
  const intl = useIntl();

  const options = useMemo(
    () =>
      fragments
        .filter((fragment) => fragment.type !== 'layout')
        .map((fragment) => ({
          value: fragment.key,
          name: fragmentDisplayName(fragment),
          searchText: `${fragment.key} ${fragmentDisplayName(
            fragment,
          )}`.toLowerCase(),
          label: (
            <span className={styles.option}>
              <span className={styles.optionName}>
                {fragmentDisplayName(fragment)}
              </span>
              <span className={styles.optionKey}>{fragment.key}</span>
            </span>
          ),
        }))
        // By name, not key: a partner block's key is opaque, so sorting on it
        // would scatter the list at random.
        .sort((a, b) => a.name.localeCompare(b.name)),
    [fragments],
  );

  if (options.length === 0) return null;

  return (
    <Select
      size="small"
      className={styles.select}
      popupMatchSelectWidth={360}
      showSearch
      value={null}
      disabled={disabled}
      placeholder={getIntlTranslation(intl, {
        id: 'pages.emailCenter.editor.insertFragment',
        defaultMessage: 'Insert block…',
      })}
      options={options}
      filterOption={(input, option) =>
        (option?.searchText ?? '').includes(input.toLowerCase())
      }
      onSelect={(key: string) => onInsert(`{% include '${key}' %}`, key)}
    />
  );
};

export default React.memo(FragmentInsertSelect);
